import { definePreset, StyleOptions } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { PaletteStep, tonalPalette } from './palette';

/**
 * Carbon — the D-Carbonize theme, built on PrimeNG's free Aura preset.
 *
 * Everything visual a PrimeNG component needs comes from these design tokens; the global
 * stylesheet (`theme/styles`) only adds the font, the element defaults and the app frame.
 * Client builds keep this preset and swap the primary palette only (see `clients.ts`).
 *
 * Sizes are in rem against the app's 14px root: 1rem = 14px, 0.8571rem = 12px.
 */

/** D-Carbonize brand blue. Its hover colour, #275bcf, is step 600 of its tonal palette. */
export const CARBON_PRIMARY = '#2e6bf3';

const STEPS: PaletteStep[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

/** `{name.50}` … `{name.950}`: a palette made of references to a primitive scale. */
function paletteRefs(name: string): Record<PaletteStep, string> {
    return Object.fromEntries(STEPS.map((step) => [step, `{${name}.${step}}`])) as Record<PaletteStep, string>;
}

/**
 * The neutral scale: a cool grey with a hint of blue. Step 600 is the app's text slate (#545c64);
 * step 500, the muted text, still clears 4.5:1 on white and on the page background (step 100);
 * step 400 — placeholders, field icons, the outline of an unticked box — clears 3:1 on white, the
 * minimum for a control to be seen.
 */
export const CARBON_NEUTRALS: Record<PaletteStep, string> = {
    50: '#f8f9fb',
    100: '#f1f3f5',
    200: '#e4e7eb',
    300: '#d1d5db',
    400: '#878e96',
    500: '#676e77',
    600: '#545c64',
    700: '#434950',
    800: '#2f343a',
    900: '#1f2327',
    950: '#131619',
};

const surface = { 0: '#ffffff', ...paletteRefs('graphite') };

/** A tag tinted like the app's `.pill`: a 12% tint of the hue behind, the hue a quarter darker in front. */
function pillColors(hue: string) {
    return {
        background: `color-mix(in srgb, ${hue}, transparent 88%)`,
        color: `color-mix(in srgb, ${hue}, black 25%)`,
    };
}

/**
 * A filled severity button: `step` at rest, one step darker on hover and two when pressed. Steps
 * are picked so the white label clears 4.5:1 — Aura's 500 steps fall to 2–3:1 on green and sky.
 */
function solidButton(hue: string, step: 600 | 700) {
    const [rest, hover, active] = [step, step + 100, step + 200].map((s) => `{${hue}.${s}}`);
    return {
        background: rest,
        hoverBackground: hover,
        activeBackground: active,
        borderColor: rest,
        hoverBorderColor: hover,
        activeBorderColor: active,
        focusRing: { color: rest, shadow: 'none' },
    };
}

/** Message and toast tones: tinted background, text one step darker than Aura's for contrast. */
function feedbackTone(hue: string, step: 600 | 700) {
    const text = `{${hue}.${step}}`;
    return { color: text, outlined: { color: text, borderColor: text }, simple: { color: text } };
}

/**
 * The table look shared by p-table and p-treeTable: a plain header (small muted labels on the
 * table's own surface, a single rule below), faint row separators, a sort arrow shown on hover
 * or once the column is sorted, rows that keep their text colour when hovered or selected, and
 * the paginator on the right with its page count on the left.
 */
function tableTokens(name: 'datatable' | 'treetable', sortableColumn: string) {
    const prefix = `.p-${name}`;
    return {
        header: { borderWidth: '0', padding: '0.75rem 1rem' },
        headerCell: {
            background: '{content.background}',
            hoverBackground: '{content.background}',
            selectedBackground: '{content.background}',
            borderColor: '{surface.200}',
            color: '{text.muted.color}',
            hoverColor: '{text.color}',
            selectedColor: '{text.color}',
            padding: '0.5rem 1rem',
        },
        columnTitle: { fontWeight: '500' },
        row: { hoverBackground: '{surface.50}', hoverColor: '{text.color}', selectedColor: '{text.color}' },
        bodyCell: { borderColor: '{surface.100}', padding: '0.75rem 1rem' },
        footerCell: { padding: '0.75rem 1rem' },
        sortIcon: { color: '{text.muted.color}', hoverColor: '{text.color}', size: '0.75rem' },
        paginatorBottom: { borderWidth: '0' },
        // Aura's own table CSS (the loading mask) is restated here, as this replaces it.
        css: ({ dt }: StyleOptions) => `
            ${prefix}-thead > tr > th {
                font-size: 0.8571rem;
            }
            ${prefix}-tbody > tr > td {
                font-variant-numeric: tabular-nums;
            }
            ${prefix}-tbody > tr:last-child > td {
                border-bottom-color: transparent;
            }
            ${prefix}-tfoot > tr > td {
                border-top: 1px solid ${dt('surface.200')};
                font-weight: 600;
                font-variant-numeric: tabular-nums;
            }
            ${sortableColumn}:not(${prefix}-column-sorted) ${prefix}-sort-icon {
                opacity: 0;
                transition: opacity ${dt('transition.duration')};
            }
            ${sortableColumn}:hover ${prefix}-sort-icon,
            ${sortableColumn}:focus-visible ${prefix}-sort-icon {
                opacity: 1;
            }
            ${prefix} ${prefix}-thead > tr > th${prefix}-column-sorted ${prefix}-sort-icon {
                color: ${dt('primary.color')};
            }
            ${prefix} .p-paginator {
                justify-content: flex-end;
            }
            ${prefix} .p-paginator-current {
                margin-inline-end: auto;
            }
            ${prefix}-mask.p-overlay-mask {
                --px-mask-background: light-dark(rgba(255, 255, 255, 0.5), rgba(0, 0, 0, 0.3));
            }
        `,
    };
}

const shadow = {
    control: '0 1px 2px 0 rgba(19, 22, 25, 0.04)',
    panel: '0 1px 3px 0 rgba(19, 22, 25, 0.06), 0 1px 2px -1px rgba(19, 22, 25, 0.04)',
    overlay: '0 12px 28px -6px rgba(19, 22, 25, 0.18), 0 2px 6px -2px rgba(19, 22, 25, 0.1)',
    modal: '0 24px 48px -12px rgba(19, 22, 25, 0.22)',
};

export const CarbonPreset = definePreset(Aura, {
    primitive: {
        borderRadius: { none: '0', xs: '2px', sm: '4px', md: '8px', lg: '10px', xl: '12px' },
        // The primary scale. A client build swaps it for its own colour (see `clients.ts`).
        brand: tonalPalette(CARBON_PRIMARY),
        graphite: CARBON_NEUTRALS,
    },
    semantic: {
        transitionDuration: '0.15s',
        primary: paletteRefs('brand'),
        focusRing: { width: '2px', style: 'solid', color: '{primary.color}', offset: '2px', shadow: 'none' },
        formField: {
            paddingX: '0.75rem',
            paddingY: '0.5rem',
            borderRadius: '{border.radius.md}',
            // Fields show focus with a soft halo in the primary colour rather than an outline.
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
                shadow: '0 0 0 3px color-mix(in srgb, {primary.color}, transparent 80%)',
            },
        },
        content: { borderRadius: '{border.radius.md}' },
        overlay: {
            select: { borderRadius: '{border.radius.md}', shadow: shadow.overlay },
            popover: { borderRadius: '{border.radius.md}', padding: '0.875rem', shadow: shadow.overlay },
            modal: { borderRadius: '{border.radius.xl}', padding: '1.5rem', shadow: shadow.modal },
            navigation: { shadow: shadow.overlay },
        },
        colorScheme: {
            light: {
                surface,
                primary: {
                    color: '{primary.500}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}',
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}',
                },
                mask: { background: 'rgba(19, 22, 25, 0.45)', color: '{surface.200}' },
                // Menus and pickers open over white panels: a firmer edge than the panels' own.
                overlay: {
                    select: { borderColor: '{surface.300}' },
                    popover: { borderColor: '{surface.300}' },
                },
                formField: {
                    disabledBackground: '{surface.100}',
                    borderColor: '{surface.300}',
                    hoverBorderColor: '{surface.400}',
                    color: '{text.color}',
                    disabledColor: '{surface.500}',
                    invalidBorderColor: '{red.500}',
                    placeholderColor: '{surface.400}',
                    iconColor: '{surface.400}',
                    shadow: shadow.control,
                },
                text: {
                    color: '{surface.600}',
                    hoverColor: '{surface.700}',
                    mutedColor: '{surface.500}',
                    hoverMutedColor: '{surface.600}',
                },
                // Carbon's own tokens, for what PrimeNG does not draw: `--p-app-*`.
                app: {
                    // A step darker than white, so panels (white, bordered) stand out from the page.
                    background: '{surface.100}',
                    panelShadow: shadow.panel,
                    overlayShadow: shadow.overlay,
                    errorColor: '{red.600}',
                    // Status colours for the app's own marks (values, pills, banners): the text
                    // colour clears 4.5:1 on white and on its own soft background.
                    success: { color: '{green.700}', background: '{green.50}' },
                    info: { color: '{sky.700}', background: '{sky.50}' },
                    warn: { color: '{orange.700}', background: '{orange.50}' },
                    danger: { color: '{red.600}', background: '{red.50}' },
                },
            },
            // Dark mode is designed for but not shipped: Aura's dark tokens on the Carbon neutrals.
            dark: {
                surface,
                app: {
                    background: '{surface.950}',
                    panelShadow: 'none',
                    overlayShadow: shadow.overlay,
                    errorColor: '{red.400}',
                    success: { color: '{green.400}', background: 'color-mix(in srgb, {green.400}, transparent 84%)' },
                    info: { color: '{sky.400}', background: 'color-mix(in srgb, {sky.400}, transparent 84%)' },
                    warn: { color: '{orange.400}', background: 'color-mix(in srgb, {orange.400}, transparent 84%)' },
                    danger: { color: '{red.400}', background: 'color-mix(in srgb, {red.400}, transparent 84%)' },
                },
            },
        },
    },
    components: {
        button: {
            root: { paddingX: '1rem', label: { fontWeight: '500' } },
            colorScheme: {
                light: {
                    // Secondary is the neutral, bordered button found in most SaaS products.
                    root: {
                        secondary: {
                            background: '{surface.0}',
                            hoverBackground: '{surface.50}',
                            activeBackground: '{surface.100}',
                            borderColor: '{surface.300}',
                            hoverBorderColor: '{surface.400}',
                            activeBorderColor: '{surface.400}',
                            color: '{text.color}',
                            hoverColor: '{text.hover.color}',
                            activeColor: '{text.hover.color}',
                            focusRing: { color: '{primary.color}', shadow: 'none' },
                        },
                        success: solidButton('green', 700),
                        info: solidButton('sky', 700),
                        warn: solidButton('orange', 700),
                        danger: solidButton('red', 600),
                        help: solidButton('purple', 600),
                        contrast: {
                            background: '{surface.800}',
                            hoverBackground: '{surface.700}',
                            activeBackground: '{surface.600}',
                            borderColor: '{surface.800}',
                            hoverBorderColor: '{surface.700}',
                            activeBorderColor: '{surface.600}',
                            focusRing: { color: '{surface.800}', shadow: 'none' },
                        },
                    },
                    outlined: {
                        secondary: { borderColor: '{surface.300}', color: '{text.color}' },
                        success: { color: '{green.700}' },
                        info: { color: '{sky.700}' },
                        warn: { color: '{orange.700}' },
                        danger: { color: '{red.600}' },
                    },
                    text: {
                        success: { color: '{green.700}' },
                        info: { color: '{sky.700}' },
                        warn: { color: '{orange.700}' },
                        danger: { color: '{red.600}' },
                    },
                },
            },
        },
        // An unticked box, an empty radio and a switch that is off are drawn by their outline alone,
        // so it takes the 3:1 grey rather than the fields' light border.
        checkbox: { root: { borderColor: '{surface.400}', hoverBorderColor: '{surface.500}' } },
        radiobutton: { root: { borderColor: '{surface.400}', hoverBorderColor: '{surface.500}' } },
        toggleswitch: {
            colorScheme: {
                light: { root: { background: '{surface.400}', hoverBackground: '{surface.500}' } },
            },
        },
        // A select button is a segmented control: its options share one track, a step darker than the
        // page grey so it shows on a panel and on the page alike; the chosen option is a white chip on it.
        togglebutton: {
            // An option that cannot be picked stays on the bare track, its label two steps paler: no
            // lighter block of its own, which read as a second, selectable group.
            root: {
                disabledBackground: '{surface.200}',
                disabledBorderColor: '{surface.200}',
                disabledColor: '{surface.400}',
            },
            icon: { disabledColor: '{surface.400}' },
            colorScheme: {
                light: {
                    root: {
                        background: '{surface.200}',
                        checkedBackground: '{surface.200}',
                        hoverBackground: '{surface.200}',
                        borderColor: '{surface.200}',
                        checkedBorderColor: '{surface.200}',
                        color: '{surface.600}',
                        hoverColor: '{surface.800}',
                        checkedColor: '{surface.700}',
                    },
                    icon: { color: '{surface.600}', hoverColor: '{surface.800}', checkedColor: '{surface.700}' },
                },
            },
        },
        avatar: {
            root: { background: '{primary.100}', color: '{primary.700}', fontSize: '0.8571rem' },
            group: { offset: '-0.5rem' },
        },
        card: {
            root: { borderRadius: '{border.radius.xl}', shadow: shadow.panel },
            body: { padding: '1.5rem', gap: '0.75rem' },
            title: { fontSize: '1.1429rem', fontWeight: '600' },
            css: ({ dt }) => `
                .p-card {
                    border: 1px solid ${dt('content.border.color')};
                }
            `,
        },
        // A chip is a small label (an entity, a tag, a filter): the size of a tag, in the text colour.
        // A long label ends in an ellipsis, so a row of chips can shrink to the width it is given.
        chip: {
            root: { borderRadius: '{border.radius.sm}', paddingX: '0.5rem', paddingY: '0.125rem', gap: '0.25rem' },
            icon: { size: '0.8571rem' },
            removeIcon: { size: '0.8571rem' },
            colorScheme: {
                light: {
                    root: { background: '{surface.100}', color: '{text.color}' },
                    icon: { color: '{text.muted.color}' },
                    removeIcon: { color: '{text.muted.color}' },
                },
            },
            css: () => `
                .p-chip {
                    min-width: 0;
                    font-size: 0.9286rem;
                    font-weight: 500;
                    line-height: 1.5;
                }
                .p-chip-label {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            `,
        },
        datatable: tableTokens('datatable', '.p-datatable-sortable-column'),
        // Tree tables mark sortable headers with the older `p-sortable-column` class.
        treetable: tableTokens('treetable', '.p-sortable-column'),
        dialog: {
            header: { padding: '1.25rem 1.5rem' },
            title: { fontSize: '1.1429rem', fontWeight: '600' },
            content: { padding: '0 1.5rem 1.5rem 1.5rem' },
            footer: { padding: '0 1.5rem 1.5rem 1.5rem', gap: '0.5rem' },
        },
        drawer: {
            header: { padding: '1.25rem 1.5rem' },
            title: { fontSize: '1.1429rem', fontWeight: '600' },
            content: { padding: '0 1.5rem 1.5rem 1.5rem' },
            footer: { padding: '1.25rem 1.5rem' },
            // Buttons in the footer line up as in a dialog's: right-aligned, the main action last.
            css: () => `
                .p-drawer-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                }
            `,
        },
        // The app's fieldsets are "more information" disclosures: a link-coloured toggle with the
        // help text opening below it, set off by a rule — no box around a collapsed, empty legend.
        fieldset: {
            root: { background: 'transparent', borderColor: 'transparent', borderRadius: '0', padding: '0' },
            legend: {
                background: 'transparent',
                hoverBackground: 'transparent',
                color: '{primary.color}',
                hoverColor: '{primary.hover.color}',
                borderRadius: '{border.radius.sm}',
                padding: '0.25rem 0',
                gap: '0.25rem',
                fontWeight: '500',
            },
            toggleIcon: { color: '{primary.color}', hoverColor: '{primary.hover.color}' },
            content: { padding: '0.25rem 0 0.5rem 1rem' },
            css: ({ dt }: StyleOptions) => `
                .p-fieldset {
                    margin-bottom: 0.5rem;
                }
                .p-fieldset-legend {
                    font-size: 0.9286rem;
                }
                .p-fieldset-toggle-button {
                    color: inherit;
                }
                .p-fieldset-content {
                    margin-left: 0.5rem;
                    border-left: 2px solid ${dt('surface.200')};
                    color: ${dt('text.muted.color')};
                }
            `,
        },
        message: {
            colorScheme: {
                light: {
                    success: feedbackTone('green', 700),
                    warn: feedbackTone('yellow', 700),
                    error: feedbackTone('red', 700),
                },
            },
        },
        paginator: {
            navButton: { width: '2.25rem', height: '2.25rem', borderRadius: '{border.radius.md}' },
        },
        progressspinner: {
            colorScheme: {
                light: {
                    root: {
                        colorOne: '{primary.color}',
                        colorTwo: '{primary.color}',
                        colorThree: '{primary.color}',
                        colorFour: '{primary.color}',
                    },
                },
            },
        },
        slider: {
            // A blue ring around the white knob, so the handle reads as the thing to drag.
            handle: { background: '{primary.color}', hoverBackground: '{primary.hover.color}' },
        },
        tabs: {
            // Tabs take the surface they sit on: white inside a panel, the page grey above one.
            tablist: { background: 'transparent' },
            tab: { padding: '0.75rem 1rem', fontWeight: '500' },
            tabpanel: { background: 'transparent' },
            activeBar: { height: '2px' },
        },
        tag: {
            root: {
                fontSize: '0.8571rem',
                fontWeight: '600',
                padding: '0.125rem 0.5rem',
                borderRadius: '{border.radius.sm}',
            },
            icon: { size: '1rem' },
            // Same colours as the app's `.pill`, so a status tag and a category pill read as one family.
            colorScheme: {
                light: {
                    primary: pillColors('{primary.color}'),
                    success: pillColors('{green.600}'),
                    info: pillColors('{sky.600}'),
                    warn: pillColors('{orange.500}'),
                    danger: pillColors('{red.600}'),
                },
            },
        },
        tooltip: {
            root: { maxWidth: '20rem', padding: '0.375rem 0.625rem' },
            colorScheme: { light: { root: { background: '{surface.600}', color: '{surface.0}' } } },
        },
        toast: {
            root: { width: '24rem' },
            colorScheme: {
                light: {
                    success: { color: '{green.700}' },
                    warn: { color: '{yellow.700}' },
                    error: { color: '{red.700}' },
                },
            },
        },
    },
});
